// ---------------------------------------------------------------------------
// Live monitoring reducer — SP-5f Task B1
// Pure reducer; no side effects. State uses Map<K, V> for O(1) lookups.
// ---------------------------------------------------------------------------

export type ConnectionStatus = 'connecting' | 'snapshot' | 'live' | 'reconnecting' | 'disconnected';

export interface Session {
  sessionId: string;
  operatorUserId: number;
  operatorDisplayName: string;
  missionId: number | null;
  pickListId: number | null;
  pickListExternalId: string | null;
  pickListRecipient: string | null;
  currentTaskId: number | null;
  currentTaskLocation: string | null;
  currentTaskItemName: string | null;
  qtyRequired: number | null;
  qtyPicked: number | null;
  fsmState: string | null;
  isMuted: boolean;
  isPaused: boolean;
  startedAt: string;
  lastEventAt: string;
  /** Mission progress — done / total task counts (cancelled tasks excluded
   *  from total). Both null until the first task.opened event arrives or
   *  the snapshot fills them in. */
  tasksDone: number | null;
  tasksTotal: number | null;
  /** SP-8c — true quando l'operatore si è disconnesso (session.ended). La card
   *  resta visibile con l'ultimo avanzamento noto + il timestamp `lastEventAt`.
   *  undefined/false = sessione attiva (online). */
  offline?: boolean;
}

export interface PickListLite {
  pickListId: number;
  externalId: string;
  recipient: string | null;
  status: 'open' | 'in_progress' | 'fulfilled' | 'confirmed' | 'cancelled' | 'archived';
  totalMissions: number;
  activeMissions: number;
}

export interface LiveState {
  sessions: Map<string, Session>;
  pickLists: Map<number, PickListLite>;
  /** The `id` field of the last SSE event applied (for Last-Event-ID reconnect). */
  lastEventAt: string | null;
  connectionStatus: ConnectionStatus;
}

export const initialLiveState: LiveState = {
  sessions: new Map(),
  pickLists: new Map(),
  lastEventAt: null,
  connectionStatus: 'connecting',
};

// ---------------------------------------------------------------------------
// Snapshot shape (mirrors SnapshotSchema in live.ts)
// ---------------------------------------------------------------------------

export interface SnapshotData {
  warehouseId: number;
  snapshotAt: string;
  sessions: Session[];
  pickListsInProgress: PickListLite[];
}

// ---------------------------------------------------------------------------
// Action types
// ---------------------------------------------------------------------------

export type InitAction = {
  type: 'init';
  snapshot: SnapshotData;
};

export type SseAction = {
  type: 'sse';
  /** The SSE event's `id:` field (used as Last-Event-ID). */
  id: string;
  /** The SSE event's `event:` field (e.g. "session.started"). */
  event: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
};

export type LiveAction = InitAction | SseAction;

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export function liveReducer(state: LiveState, action: LiveAction): LiveState {
  switch (action.type) {
    case 'init': {
      const sessions = new Map<string, Session>();
      // SP-8c — lo snapshot contiene solo le sessioni ATTIVE. Preserva le
      // sessioni OFFLINE già in stato (operatori disconnessi con l'ultimo
      // avanzamento), altrimenti un re-init (reconnect SSE / refresh snapshot)
      // le cancellerebbe e la card perderebbe i dati di avanzamento. Una
      // sessione offline viene scartata solo se lo stesso operatore è di nuovo
      // attivo nello snapshot.
      const activeOpIds = new Set(action.snapshot.sessions.map((s) => s.operatorUserId));
      for (const [sid, s] of state.sessions) {
        if (s.offline === true && !activeOpIds.has(s.operatorUserId)) {
          sessions.set(sid, s);
        }
      }
      for (const s of action.snapshot.sessions) {
        sessions.set(s.sessionId, s);
      }
      const pickLists = new Map<number, PickListLite>();
      for (const pl of action.snapshot.pickListsInProgress) {
        pickLists.set(pl.pickListId, pl);
      }
      return {
        ...state,
        sessions,
        pickLists,
        lastEventAt: null,
      };
    }

    case 'sse': {
      const { id, event, data } = action;
      const nextState = applySseEvent(state, event, data);
      return { ...nextState, lastEventAt: id };
    }

    default:
      return state;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applySseEvent(state: LiveState, event: string, data: any): LiveState {
  switch (event) {
    case 'session.started': {
      // Voice-gateway publishes a minimal payload: snake_case fields with
      // just session_id / operator_user_id / mission_id. The reducer used
      // to cast `data as Session`, which silently produced an entry under
      // sessions.get(undefined) — meaning subsequent fsm.transition /
      // task.opened deltas for the new session_id hit a Map miss and were
      // dropped. Build a real stub here (camelCase) and let later events
      // (task.opened, fsm.transition) fill in the fields they own.
      const d = data as {
        session_id: string;
        operator_user_id: number;
        operator_display_name?: string | null;
        mission_id: number | null;
      };
      if (state.sessions.has(d.session_id)) return state;
      const sessions = new Map(state.sessions);
      // SP-8c — riconnessione: rimuovi un'eventuale sessione OFFLINE precedente
      // dello stesso operatore, altrimenti comparirebbe due volte (online +
      // offline). L'operatore ha al più una sessione attiva (vincolo DB).
      for (const [sid, s] of sessions) {
        if (s.operatorUserId === d.operator_user_id) sessions.delete(sid);
      }
      sessions.set(d.session_id, {
        sessionId: d.session_id,
        operatorUserId: d.operator_user_id,
        operatorDisplayName: d.operator_display_name ?? `Operatore #${d.operator_user_id}`,
        missionId: d.mission_id,
        pickListId: null,
        pickListExternalId: null,
        pickListRecipient: null,
        currentTaskId: null,
        currentTaskLocation: null,
        currentTaskItemName: null,
        qtyRequired: null,
        qtyPicked: null,
        fsmState: null,
        isMuted: false,
        isPaused: false,
        startedAt: new Date().toISOString(),
        lastEventAt: new Date().toISOString(),
        tasksDone: null,
        tasksTotal: null,
      });
      return { ...state, sessions };
    }

    case 'session.ended': {
      const sessionId = (data as { session_id: string }).session_id;
      const existing = state.sessions.get(sessionId);
      if (!existing) return state;
      const sessions = new Map(state.sessions);
      // SP-8c — NON eliminare: marca la sessione come offline e conserva
      // l'ultimo avanzamento noto + il timestamp dell'ultimo aggiornamento.
      // La card resta visibile (operatore disconnesso) finché non riparte.
      sessions.set(sessionId, { ...existing, offline: true, lastEventAt: new Date().toISOString() });
      return { ...state, sessions };
    }

    case 'fsm.transition': {
      const { session_id, to } = data as { session_id: string; to: string };
      const existing = state.sessions.get(session_id);
      if (!existing) return state;
      const sessions = new Map(state.sessions);
      sessions.set(session_id, { ...existing, fsmState: to });
      return { ...state, sessions };
    }

    case 'task.confirmed': {
      const { session_id, qty_picked } = data as { session_id: string; qty_picked: number };
      const existing = state.sessions.get(session_id);
      if (!existing) return state;
      const sessions = new Map(state.sessions);
      // tasksDone avanza ad OGNI conferma — incluso l'ULTIMO task, dopo il quale
      // non viene emesso alcun task.opened (l'unico altro evento che porta la
      // progress). Senza questo la card resta a (total-1)/total ("non conteggia
      // l'ultimo task"). Cap a tasksTotal; task.opened ri-allinea su ogni nuovo task.
      const nextDone = existing.tasksDone != null
        ? Math.min(existing.tasksTotal ?? existing.tasksDone + 1, existing.tasksDone + 1)
        : existing.tasksDone;
      sessions.set(session_id, { ...existing, qtyPicked: qty_picked, tasksDone: nextDone });
      return { ...state, sessions };
    }

    case 'task.opened': {
      const d = data as {
        session_id: string;
        current_task_id: number;
        current_task_location: string;
        current_task_item_name: string | null;
        qty_required: number;
        qty_picked: number | null;
        mission_id?: number | null;
        pick_list_id?: number | null;
        pick_list_external_id?: string | null;
        pick_list_recipient?: string | null;
        tasks_done?: number | null;
        tasks_total?: number | null;
      };
      const existing = state.sessions.get(d.session_id);
      if (!existing) return state;
      const sessions = new Map(state.sessions);
      sessions.set(d.session_id, {
        ...existing,
        currentTaskId: d.current_task_id,
        currentTaskLocation: d.current_task_location,
        currentTaskItemName: d.current_task_item_name,
        qtyRequired: d.qty_required,
        qtyPicked: d.qty_picked,
        // Backfill mission/pick-list fields when carried — first task.opened
        // for a session created via session.started stub typically has them
        // null. Don't overwrite with null when the field is absent.
        ...(d.mission_id !== undefined && { missionId: d.mission_id }),
        ...(d.pick_list_id !== undefined && { pickListId: d.pick_list_id }),
        ...(d.pick_list_external_id !== undefined && { pickListExternalId: d.pick_list_external_id }),
        ...(d.pick_list_recipient !== undefined && { pickListRecipient: d.pick_list_recipient }),
        ...(d.tasks_done !== undefined && { tasksDone: d.tasks_done }),
        ...(d.tasks_total !== undefined && { tasksTotal: d.tasks_total }),
      });
      return { ...state, sessions };
    }

    case 'task.forced': {
      const { session_id, qty } = data as { session_id: string; qty: number };
      const existing = state.sessions.get(session_id);
      if (!existing) return state;
      const sessions = new Map(state.sessions);
      sessions.set(session_id, { ...existing, qtyPicked: qty });
      return { ...state, sessions };
    }

    case 'mute.changed': {
      const { session_id, muted } = data as { session_id: string; muted: boolean };
      const existing = state.sessions.get(session_id);
      if (!existing) return state;
      const sessions = new Map(state.sessions);
      sessions.set(session_id, { ...existing, isMuted: muted });
      return { ...state, sessions };
    }

    case 'pause.changed': {
      const { session_id, paused } = data as { session_id: string; paused: boolean };
      const existing = state.sessions.get(session_id);
      if (!existing) return state;
      const sessions = new Map(state.sessions);
      sessions.set(session_id, { ...existing, isPaused: paused });
      return { ...state, sessions };
    }

    case 'pick_list.imported': {
      const pl = data as PickListLite;
      const pickLists = new Map(state.pickLists);
      pickLists.set(pl.pickListId, pl);
      return { ...state, pickLists };
    }

    case 'pick_list.assigned': {
      const { pick_list_id } = data as { pick_list_id: number };
      const existing = state.pickLists.get(pick_list_id);
      if (!existing) return state;
      const pickLists = new Map(state.pickLists);
      pickLists.set(pick_list_id, { ...existing, activeMissions: existing.activeMissions + 1 });
      return { ...state, pickLists };
    }

    case 'pick_list.confirmed':
    case 'pick_list.cancelled': {
      const { pick_list_id } = data as { pick_list_id: number };
      if (!state.pickLists.has(pick_list_id)) return state;
      const pickLists = new Map(state.pickLists);
      pickLists.delete(pick_list_id);
      return { ...state, pickLists };
    }

    // Deferred or unhandled events are ignored
    default:
      return state;
  }
}
